import { useMemo } from "react";
import type { ReactNode } from "react";
import { Link } from "wouter";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Lock, Sparkles } from "lucide-react";

type SubscriptionTier = "verified_trader" | "pro_seller";

type PaywallPerk = {
  label: string;
  description?: string;
};

type PaywallProps = {
  tier: SubscriptionTier;
  title?: string;
  subtitle?: string;
  perks?: PaywallPerk[];
  ctaLabel?: string;
  ctaHref?: string;
  onCtaClick?: () => void;
  className?: string;
  children?: ReactNode;
};

type SubscriptionRecord = {
  planType: SubscriptionTier;
  status: string;
};

type DefaultCopy = {
  title: string;
  subtitle: string;
  ctaLabel: string;
  perks: PaywallPerk[];
};

const DEFAULT_COPY: Record<SubscriptionTier, DefaultCopy> = {
  verified_trader: {
    title: "Verified Trader",
    subtitle: "Earn trust instantly with a verification badge, early access to listings, and ad-free browsing.",
    ctaLabel: "Join Verified Trader",
    perks: [
      { label: "Verification badge on your profile" },
      { label: "Access new listings 60 minutes early" },
      { label: "Ad-free browsing experience" },
      { label: "Priority safety support" },
    ],
  },
  pro_seller: {
    title: "Pro Seller",
    subtitle: "Unlock analytics, automated boosts, and Salesman AI credits tailored for power sellers.",
    ctaLabel: "Upgrade to Pro Seller",
    perks: [
      { label: "Advanced performance analytics dashboard" },
      { label: "Auto-bump and featured placement credits" },
      { label: "Monthly Salesman AI conversation credits" },
      { label: "Team-ready inbox with priority support" },
    ],
  },
};

function LoadingCard({ className }: { className?: string }) {
  return (
    <Card className={cn("border-dashed", className)}>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-64 mt-3" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-56" />
        </div>
      </CardContent>
      <CardFooter>
        <Skeleton className="h-10 w-40" />
      </CardFooter>
    </Card>
  );
}

export function Paywall({
  tier,
  title,
  subtitle,
  perks,
  ctaLabel,
  ctaHref,
  onCtaClick,
  className,
  children,
}: PaywallProps) {
  const copy = DEFAULT_COPY[tier];
  const activeTitle = title ?? copy.title;
  const activeSubtitle = subtitle ?? copy.subtitle;
  const activePerks = perks ?? copy.perks;
  const activeCtaLabel = ctaLabel ?? copy.ctaLabel;
  const destination = ctaHref ?? (onCtaClick ? undefined : "/pricing");

  const currentUser = useQuery(api.auth.getCurrentUser);
  const subscriptionArgs = currentUser && currentUser._id ? { userId: currentUser._id as Id<"users"> } : undefined;
  const subscription = useQuery(
    api.payments.getSubscription,
    subscriptionArgs ? subscriptionArgs : "skip"
  ) as SubscriptionRecord | null | undefined;

  const isLoading = currentUser === undefined || (subscriptionArgs && subscription === undefined);
  if (isLoading) {
    return <LoadingCard className={className} />;
  }

  const requiresAuth = !currentUser;
  const hasActiveSubscription = Boolean(subscription && subscription.status === "active");
  const planType = subscription?.planType;
  const hasAccess = hasActiveSubscription && (planType === tier || (tier === "verified_trader" && planType === "pro_seller"));
  const showLockedState = requiresAuth || !hasAccess;

  const blurPreview = useMemo(() => {
    if (!children) return null;
    return (
      <div className="relative">
        <div className={cn("rounded-xl border border-border/60 p-4 transition", showLockedState && "pointer-events-none select-none blur-sm opacity-40")}>{children}</div>
        {showLockedState && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-background/85 px-3 py-1 text-xs font-medium shadow">Preview</span>
          </div>
        )}
      </div>
    );
  }, [children, showLockedState]);

  const renderCta = () => {
    if (!showLockedState) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 text-primary" />
          <span>Your plan is active. Enjoy your premium tools.</span>
        </div>
      );
    }

    if (requiresAuth) {
      return (
        <Button asChild className="w-full sm:w-auto">
          <Link href="/sign-in">Sign in to unlock</Link>
        </Button>
      );
    }

    if (destination) {
      return (
        <Button
          className="w-full sm:w-auto"
          variant="default"
          asChild
        >
          <Link href={destination} onClick={onCtaClick}>
            {activeCtaLabel}
          </Link>
        </Button>
      );
    }

    return (
      <Button className="w-full sm:w-auto" onClick={onCtaClick}>
        {activeCtaLabel}
      </Button>
    );
  };

  return (
    <Card className={cn("relative overflow-hidden border-dashed border-primary/40 bg-muted/20", className)}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Lock className="h-5 w-5 text-primary" />
              {activeTitle}
            </CardTitle>
            <CardDescription className="mt-2 text-sm text-muted-foreground">
              {activeSubtitle}
            </CardDescription>
          </div>
          {!showLockedState && (
            <Badge variant="secondary">Active</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {children && blurPreview}
        {showLockedState && (
          <div className={cn(children ? "mt-6" : "")}>
            <ul className="space-y-3 text-sm">
              {activePerks.map((perk) => (
                <li key={perk.label} className="flex items-start gap-2">
                  <Sparkles className="mt-1 h-4 w-4 text-primary" />
                  <div>
                    <span className="font-medium text-foreground">{perk.label}</span>
                    {perk.description && (
                      <p className="text-xs text-muted-foreground">{perk.description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-muted-foreground">
          {showLockedState ? "Cancel anytime. Instant activation." : "Thank you for supporting the safety-first marketplace."}
        </div>
        {renderCta()}
      </CardFooter>
    </Card>
  );
}

export default Paywall;
